import { Component, ElementRef, computed, effect, inject, signal, viewChild } from '@angular/core';

import {
  borderClasses,
  colorClasses,
  generalClasses,
  layoutClasses,
  layoutTierPreviewWidths,
  layoutTiers,
  spacingClasses,
  textClasses,
} from '@demo/helper-classes/helper-classes.consts';
import { CipoFormModule } from '@shared/modules';
import { NotificationService } from '@shared/services';

import { prepareViewportFrame, resizeViewportFrame } from './viewport-frame';

@Component({
  selector: 'cipo-helper-classes',
  imports: [CipoFormModule],
  templateUrl: './helper-classes.component.html',
  styleUrl: './helper-classes.component.scss',
})
export class HelperClassesComponent {
  notify = inject(NotificationService);
  test = 'test';
  // spacings
  SPACING_CLASSES = spacingClasses;
  spacingType = 'm';
  spacingPosition = 'a';
  spacingValue = 'auto';

  // colors
  COLOR_CLASSES = colorClasses;
  colorType = 'bg';
  colorPalette = 'primary';
  hue = '900';

  // typography
  TEXT_CLASSES = textClasses;
  textType = 'cipo-text';
  textValue = 'label';

  //   borders
  BORDERS_CLASSES = borderClasses;
  borderType = '-radius';
  borderValue = 'xs';

  //   general
  GENERAL_CLASSES = generalClasses;
  generalType = 'position';
  generalValue = 'absolute';

  //   layout
  LAYOUT_CLASSES = layoutClasses;
  LAYOUT_TIERS = layoutTiers;
  layoutType = signal('flex-item');
  layoutValue = signal('50');
  layoutTier = signal('');
  layoutPreviewWidth = computed(() => layoutTierPreviewWidths[this.layoutTier()]);
  layoutClass = computed(() => layoutClasses[this.layoutType()].prefix + this.layoutValue() + this.layoutTier());
  layoutOnContainer = computed(() => ['container', 'grid'].includes(layoutClasses[this.layoutType()].target));
  private readonly layoutFrame = viewChild<ElementRef<HTMLIFrameElement>>('layoutFrame');

  constructor() {
    effect(() => {
      const frame = this.layoutFrame()?.nativeElement;
      if (frame) {
        this.renderLayoutPreview(frame, this.layoutClass(), this.layoutPreviewWidth());
      }
    });
  }

  onTextTypeChange(value: string) {
    this.textValue = Object.keys(textClasses[value].values)[0];
  }

  onBorderTypeChange(value: string) {
    this.borderValue = Object.keys(borderClasses[value].values)[0];
  }

  onLayoutTypeChange(value: string) {
    this.layoutType.set(value);
    this.layoutValue.set(Object.keys(layoutClasses[value].values)[0]);
  }

  onGeneralTypeChange(value: string) {
    this.generalValue = Object.keys(generalClasses[value].values)[0];
  }

  copyContent(content: string) {
    navigator.clipboard.writeText(content).then(() => this.notify.success('Copied to clipboard!'));
  }

  private renderLayoutPreview(frame: HTMLIFrameElement, layoutClass: string, width: number) {
    const view = prepareViewportFrame(frame);
    resizeViewportFrame(frame, width);

    const onContainer = this.layoutOnContainer();
    const base = {
      container: 'cipo--flex--row',
      item: 'cipo--flex--row',
      grid: this.layoutTier() ? 'cipo--grid cipo--grid--cols-4' : 'cipo--grid',
      'grid-item': 'cipo--grid cipo--grid--cols-4',
    }[layoutClasses[this.layoutType()].target];

    const align = ['align', 'align-self'].includes(this.layoutType()) ? '' : 'cipo--align--start';

    const container = view.createElement('div');
    container.className = `${base} ${align} ${onContainer ? layoutClass : ''} cipo--align-content--start cipo-bg--black-50 cipo--gap--sm p-a-sm`;
    Object.assign(container.style, {
      boxSizing: 'border-box',
      minHeight: '100vh',
      border: onContainer ? '2px solid var(--primary-900)' : '1px dashed var(--black-400)',
    });
    [28, 44, 36, 56].forEach((height, index) => {
      const highlighted = index === 0 && !onContainer;
      const child = view.createElement('div');
      child.className = highlighted
        ? `cipo-bg--primary-900 cipo-text--white p-x-sm p-y-xs cipo--border-radius--xs ${layoutClass}`
        : 'cipo-bg--accent-200 cipo-text--black-800 p-x-sm p-y-xs cipo--border-radius--xs';
      Object.assign(child.style, { minWidth: '48px', minHeight: `${height}px`, overflowWrap: 'anywhere' });
      child.textContent = highlighted ? layoutClass : String(index + 1);
      container.appendChild(child);
    });
    view.body.replaceChildren(container);
  }
}
